import styled from '@emotion/styled';
import { InlineAlert, theme } from '@travelnest/hui';
import * as React from 'react';
import { Navigate } from 'react-router-dom';

import { ICustomer } from '../../../data/entities/customer';
import { IProperty } from '../../../data/entities/property';
import { IPropertyScoreSummary } from '../../../data/entities/property-score';
import { ActionCard } from '../../components/action-card/ActionCard';
import { IAction } from '../../components/action/types';
import { CustomerAlerts } from '../../components/customer-alerts/CustomerAlerts';
import { Greeting } from '../../components/greeting/Greeting';
import { PropertyScoreSummaryCard } from '../../components/property-score/PropertyScoreSummaryCard';
import StyledHeading from '../../components/typography/StyledHeading';
import StyledText from '../../components/typography/StyledText';
import { VerificationCard } from '../../components/verification-card/VerificationCard';
import { actionDetailsTypes } from '../actions/details/types';
import { getLowestScoringProperty } from '../property-score/property-score-helpers';

import { InlineChurnAlert } from '../../../view/components/churn-alert';
import { GradientInfoCardWithImage } from '../../../view/components/gradient-info-card/GradientInfoCardWithImage';
import { Loader } from '../../../view/components/loader/Loader';
import { NewCancellationPolicyCard } from '../../../view/components/new-cancellation-policy-card/NewCancellationPolicyCard';
import {
	hasAtLeastOnePublishedAndVerifiedProperty,
} from '../../../view/helpers/customer';
import { usePromoCards } from '../../../view/hooks/usePromoCards';

const FeedContainer = styled.div`
	margin: 24px;
	max-width: 1220px;

	/* auto margin on ridiculously large screens to centre content */
	@media (min-width: 1524px) {
		margin: 0 auto;
	}

	/* use smaller margin on mobile +
	set right margin on sections individually */
	@media (max-width: 768px) {
		margin: 16px 0 16px 16px;
	}
`;

const FeedSection = styled.section`
	margin: 0 0 24px 0;

	/* 16px margins on mobile, plus right margin
	taken out of FeedContainer */
	@media (max-width: 768px) {
		margin: 0 16px 16px 0;
	}
`;

const PromoCardsSection = styled.section`
	margin: 0 0 24px 0;

	/* same as FeedSection but only apply the right margin on heading and description, not the  scrollable cards */
	@media (max-width: 768px) {
		margin: 0 0 16px 0;

		h3 {
			margin-right: 16px;
		}
		p {
			margin-right: 16px;
		}
	}
`;

const FeedCardGrid = styled.div`
	display: grid;
	gap: 16px;
	grid-row-gap: 16px;
	grid-template-columns: repeat(2, 1fr);

	/* use single column on mobile */
	@media (max-width: 768px) {
		grid-template-columns: 1fr;
	}
`;

const FeedSectionTitle = styled(StyledHeading)`
	color: ${theme.colors.LM_NU100};
	margin: 24px 0 0 0;
`;

const FeedSectionPrompt = styled(StyledText)`
	color: ${theme.colors.LM_NU70T};
	margin: 0 0 16px 0;
`;

const PromoCardsFlexWrapper = styled.div`
	display: flex;
	gap: 16px;
	overflow: hidden;

	/* apply margin to rightmost card so there is still a margin at the end of the scroll */
	@media (max-width: 768px) {
		overflow: scroll;

		div:nth-child(2) {
			margin-right: 16px;
		}
	}
`;

interface IHomeProps {
	creatable: boolean;
	loading?: boolean;
	loadingActions?: boolean;
	customerId: string;
	actions: IAction[];
	isLegacyCommission: boolean;
	isAdmin: boolean;
	propertyScores: IPropertyScoreSummary[];
	customerProperties: IProperty[];
	customerData?: ICustomer;
}

export const HomeComponent: React.FC<IHomeProps> = (
	{
		customerProperties,
		actions = [],
		propertyScores,
		customerData,
		customerId,
		loading: pageLoading,
		loadingActions,
		isAdmin,
	} = {} as IHomeProps
) => {
	document.title = 'Your Account | Travelnest';

	const promoCardsData = usePromoCards(customerProperties);

	const lowestScoringProperty = getLowestScoringProperty(
		customerProperties,
		propertyScores
	);
	const goingLiveAction = actions.find(
		(a) => a.type === actionDetailsTypes.ACCOUNT_VERIFICATION
	);
	const remainingActions = actions.filter(
		(a) => a.type !== actionDetailsTypes.ACCOUNT_VERIFICATION
	);

	const churnStatus = customerData?.churn?.status ?? 'active';
	const isChurned = (customerData?.churn?.status ?? 'active') === 'churned';

	if (pageLoading) {
		return <Loader loading />;
	}

	if (!hasAtLeastOnePublishedAndVerifiedProperty(customerProperties) && !isAdmin) {
		return <Navigate to="/properties" />;
	}

	return (
		<main>
			{loadingActions && <Loader loading />}
			{!loadingActions && (
				<FeedContainer>
					<FeedSection>
						{isChurned && (
							<InlineAlert
								variant="negative"
								title="Account deactivated"
								content="Your account has been deactivated. If you have any questions or wish to discuss your account, please contact our customer support team at +44 (0)333 666 6111."
							/>
						)}
						{!isChurned && (
							<>
								<Greeting name={customerData?.name} />
								<CustomerAlerts
									customerId={customerData?.id}
									properties={customerProperties}
								/>
								<FeedCardGrid>
									<InlineChurnAlert />
									{goingLiveAction &&
										churnStatus !==
											'retention_opportunity' && (
											<VerificationCard
												title={goingLiveAction.title}
											/>
										)}
									{!isAdmin && lowestScoringProperty && (
										<PropertyScoreSummaryCard
											{...lowestScoringProperty}
										/>
									)}
								</FeedCardGrid>
							</>
						)}
					</FeedSection>

					{!isChurned && (
						<PromoCardsSection>
							<NewCancellationPolicyCard
								customerId={customerId}
							/>
						</PromoCardsSection>
					)}

					{!isAdmin && !isChurned && (
						<PromoCardsSection>
							<FeedSectionTitle
								htmlElement="h3"
								lineHeight="sm"
								type="RWB120"
							>
								Don't miss out
							</FeedSectionTitle>
							<FeedSectionPrompt lineHeight="md" type="IR100">
								Make sure to check out these Travelnest features
								to increase your bookings and increase your
								margin
							</FeedSectionPrompt>
							<PromoCardsFlexWrapper>
								{promoCardsData.map((card, i) => (
									<GradientInfoCardWithImage
										key={`promo-card-${i}`}
										{...card}
									/>
								))}
							</PromoCardsFlexWrapper>
						</PromoCardsSection>
					)}
					{!isAdmin &&
						!isChurned &&
						remainingActions.length !== 0 && (
							<FeedSection>
								<FeedSectionTitle
									htmlElement="h3"
									lineHeight="sm"
									type="RWB120"
								>
									Take action
								</FeedSectionTitle>
								<FeedSectionPrompt lineHeight="md" type="IR100">
									Some suggestion actions to take which will
									help boost bookings
								</FeedSectionPrompt>
								<FeedCardGrid>
									{remainingActions.map((action, i) => (
										<ActionCard
											key={`action-${i}`}
											heading={action.title}
											paragraph={
												typeof action.text === 'string'
													? action.text
													: action.text.join(' ')
											}
											actionText={action.linkLabel!}
											actionLink={action.link!}
										/>
									))}
								</FeedCardGrid>
							</FeedSection>
						)}
				</FeedContainer>
			)}
		</main>
	);
};
